import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Download, Printer, Loader2, XCircle, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/apiClient";
import { BRAND } from "@/constants/brand";

const fmtMoney = (amt, cur = "INR") => new Intl.NumberFormat("en-IN", { style: "currency", currency: (cur || "INR").toUpperCase(), maximumFractionDigits: 0 }).format(amt || 0);

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const invoiceNumber = (params.get("invoice") || "").toUpperCase();
  const paymentId = params.get("pid") || "";
  const sessionId = params.get("session_id") || ""; // legacy Stripe support
  const [state, setState] = useState({ loading: true, paid: false, invoice: null, error: "" });
  const printRef = useRef(null);

  useEffect(() => {
    const run = async () => {
      if (sessionId) {
        // Legacy Stripe path
        try {
          const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
          const paid = data.payment_status === "paid";
          setState({ loading: false, paid, invoice: data.invoice, error: paid ? "" : (data.status === "expired" ? "Session expired." : "Payment not completed.") });
        } catch { setState({ loading: false, paid: false, invoice: null, error: "Couldn't verify payment." }); }
        return;
      }
      if (!invoiceNumber) {
        setState({ loading: false, paid: false, invoice: null, error: "No invoice provided." });
        return;
      }
      try {
        const { data } = await api.get(`/invoices/lookup/${invoiceNumber}`);
        const paid = data.payment_status === "paid";
        setState({ loading: false, paid, invoice: data, error: paid ? "" : "Invoice not yet marked paid. Refresh in a few seconds." });
      } catch {
        setState({ loading: false, paid: false, invoice: null, error: "Invoice not found." });
      }
    };
    run();
  }, [invoiceNumber, sessionId]);

  const printIt = () => window.print();

  if (state.loading) {
    return (
      <div className="section-pad container-pp text-center" data-testid="pay-success-loading">
        <Loader2 className="mx-auto animate-spin" size={42} />
        <div className="mt-6 font-display text-3xl">Confirming your payment…</div>
      </div>
    );
  }

  if (!state.paid) {
    return (
      <div className="section-pad container-pp text-center max-w-xl mx-auto" data-testid="pay-success-fail">
        <XCircle className="mx-auto text-brand-red" size={56} />
        <div className="mt-6 font-display text-4xl">Payment not completed.</div>
        <p className="mt-3 text-brand-charcoal/70">{state.error}</p>
        <Link to="/pay" className="btn-pill btn-dark mt-8 inline-flex" data-testid="ps-retry">Try again <ArrowUpRight size={14} /></Link>
      </div>
    );
  }

  const inv = state.invoice || {};
  return (
    <>
      <div className="section-pad print:hidden" data-testid="pay-success">
        <div className="container-pp">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="max-w-2xl mx-auto text-center">
            <CheckCircle2 className="mx-auto text-emerald-500" size={64} />
            <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-600 mt-5">Payment Successful</div>
            <h1 className="editorial-h1 text-5xl md:text-6xl mt-3">Thank you, {(inv.customer_name || "").split(" ")[0] || "friend"}.</h1>
            <p className="mt-4 text-brand-charcoal/70 leading-relaxed">Your payment for invoice <strong>{inv.invoice_number}</strong> has been received. A receipt is below — feel free to download, print or save it.</p>
          </motion.div>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <button onClick={printIt} className="btn-pill btn-dark text-sm" data-testid="ps-pdf"><Download size={14} /> Download PDF</button>
            <button onClick={printIt} className="btn-pill btn-outline-dark text-sm" data-testid="ps-print"><Printer size={14} /> Print receipt</button>
            <Link to="/" className="btn-pill btn-ghost text-sm border border-brand-line" data-testid="ps-home">Back to home</Link>
          </div>

          <div ref={printRef} className="mt-14 max-w-3xl mx-auto bg-white rounded-3xl border border-brand-line p-10 lg:p-12 shadow-sm" data-testid="ps-receipt">
            <ReceiptBody invoice={inv} paymentId={paymentId || inv.razorpay_payment_id || ""} />
          </div>
        </div>
      </div>

      <div className="hidden print:block p-10">
        <ReceiptBody invoice={inv} paymentId={paymentId || inv.razorpay_payment_id || ""} />
      </div>
    </>
  );
}

function ReceiptBody({ invoice, paymentId }) {
  const inv = invoice || {};
  return (
    <div>
      <div className="flex items-start justify-between gap-6 flex-wrap pb-6 border-b border-brand-line">
        <div>
          <div className="font-display text-3xl">{BRAND.brand}</div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Receipt of Payment</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Invoice</div>
          <div className="font-display text-2xl">{inv.invoice_number}</div>
          {inv.paid_at && <div className="text-xs text-brand-charcoal/55 mt-1">Paid on {inv.paid_at.slice(0,10)}</div>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6 mt-6 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Billed to</div>
          <div className="mt-1 font-medium">{inv.customer_name}</div>
          {inv.customer_phone && <div>{inv.customer_phone}</div>}
          {inv.customer_email && <div className="text-brand-charcoal/65">{inv.customer_email}</div>}
        </div>
        <div className="sm:text-right">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">From</div>
          <div className="mt-1 font-medium">{BRAND.legalName}</div>
          {BRAND.addresses?.manufacturer?.lines.map((l, i) => <div key={i} className="text-brand-charcoal/65">{l}</div>)}
        </div>
      </div>

      {inv.items?.length > 0 && (
        <div className="mt-8">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55 text-left border-b border-brand-line">
              <tr><th className="py-2">Description</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Unit</th><th className="py-2 text-right">Amount</th></tr>
            </thead>
            <tbody>
              {inv.items.map((it, i) => (
                <tr key={i} className="border-b border-brand-line last:border-0">
                  <td className="py-2">{it.description}</td>
                  <td className="py-2 text-right">{it.quantity}</td>
                  <td className="py-2 text-right">{fmtMoney(it.unit_price, inv.currency)}</td>
                  <td className="py-2 text-right">{fmtMoney(it.amount, inv.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-8 flex justify-end">
        <div className="w-full sm:w-72 space-y-2 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span>{fmtMoney(inv.subtotal, inv.currency)}</span></div>
          {inv.tax > 0 && <div className="flex justify-between"><span>Taxes</span><span>{fmtMoney(inv.tax, inv.currency)}</span></div>}
          <div className="flex justify-between font-display text-xl border-t border-brand-line pt-2 mt-2"><span>Paid</span><span className="text-emerald-600">{fmtMoney(inv.amount_paid || inv.total_amount, inv.currency)}</span></div>
        </div>
      </div>

      <div className="mt-12 pt-6 border-t border-brand-line text-xs text-brand-charcoal/55 flex flex-col sm:flex-row justify-between gap-3">
        <div>This receipt is computer-generated. No signature required. {inv.payment_method ? `Paid via ${inv.payment_method}.` : ""}</div>
        <div className="font-mono">Ref: {(paymentId || "").slice(-20) || "—"}</div>
      </div>
    </div>
  );
}
